'use client'

import { useEffect, useState, useSyncExternalStore } from 'react'
import { usePathname } from 'next/navigation'
import { isFirstPartyTrackingSurface } from '@/lib/analytics/first-party-tracking-scope'
import {
  getCookieConsentSnapshot,
  getServerCookieConsentSnapshot,
  subscribeCookieConsent,
  type CookieConsentSnapshot,
} from '@/lib/privacy/consent-runtime'

/** The page's live cookie choice (`lib/privacy/consent-runtime.ts`). Not ready on the server pass. */
export function useCookieConsent(): CookieConsentSnapshot {
  return useSyncExternalStore(subscribeCookieConsent, getCookieConsentSnapshot, getServerCookieConsentSnapshot)
}

const APP_ROOT_DOMAIN = (process.env.NEXT_PUBLIC_APP_ROOT_DOMAIN || 'maiteams.com').toLowerCase()
const LEAD_FORM_DOMAIN = (process.env.NEXT_PUBLIC_LEAD_FORM_DOMAIN || 'leads.yourdomain.com').toLowerCase()

/**
 * Whether MaiTeams' own tracking may run on the page the browser is on, by
 * the shared predicate (`lib/analytics/first-party-tracking-scope.ts`). null
 * on the server pass and until mounted, since only the browser knows its host
 * and query. Re-judged on every client navigation.
 */
export function useFirstPartyTrackingSurface(): boolean | null {
  const pathname = usePathname()
  const [judged, setJudged] = useState<{ pathname: string; surface: boolean } | null>(null)
  useEffect(() => {
    setJudged({
      pathname,
      surface: isFirstPartyTrackingSurface({
        hostname: window.location.hostname,
        pathname: window.location.pathname,
        search: window.location.search,
        appRootDomain: APP_ROOT_DOMAIN,
        leadFormDomain: LEAD_FORM_DOMAIN,
      }),
    })
  }, [pathname])
  // A client navigation must not be judged by the previous page's answer.
  if (!judged || judged.pathname !== pathname) return null
  return judged.surface
}
